import type { Application } from '@labeling/globals';
import { Button, Tag } from '@lego/connect-components-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppTreeItem } from './app-tree-item';
import type { ApplicationAccess, Selection } from './types';

export const AppTree = ({
  applications,
  selection,
  assignmentsLoaded,
  onSelectGroup,
  onSelectUnrestricted,
  onCommitNewGroup,
}: {
  applications: ApplicationAccess[];
  selection: Selection;
  assignmentsLoaded: boolean;
  onSelectGroup: (app: Application, groupName: string) => void;
  onSelectUnrestricted: (app: Application) => void;
  onCommitNewGroup: (app: Application, groupName: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});
  const [showEmptyApps, setShowEmptyApps] = useState(false);
  const [newGroupApp, setNewGroupApp] = useState<Application | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const otherAppsId = useId();
  const populatedApps = applications.filter(
    ({ groups, unrestricted }) => groups.size > 0 || unrestricted.length > 0
  );
  const emptyApps = applications.filter(
    ({ groups, unrestricted }) => groups.size === 0 && unrestricted.length === 0
  );

  useEffect(() => {
    if (!assignmentsLoaded) return;
    setExpandedApps((prev) => {
      const next = { ...prev };
      for (const { application, groups, unrestricted } of applications) {
        if (next[application] === undefined) {
          next[application] = groups.size > 0 || unrestricted.length > 0;
        }
      }
      return next;
    });
  }, [assignmentsLoaded, applications]);

  const commitNewGroup = async () => {
    if (newGroupApp && (await onCommitNewGroup(newGroupApp, newGroupName))) {
      setNewGroupApp(null);
      setNewGroupName('');
    }
  };

  const appItem = (access: ApplicationAccess) => (
    <AppTreeItem
      key={access.application}
      access={access}
      isOpen={expandedApps[access.application] ?? false}
      selection={selection}
      newGroupApp={newGroupApp}
      newGroupName={newGroupName}
      onToggle={() =>
        setExpandedApps((prev) => ({
          ...prev,
          [access.application]: !prev[access.application],
        }))
      }
      onSelectGroup={onSelectGroup}
      onSelectUnrestricted={onSelectUnrestricted}
      onStartNewGroup={() => {
        setNewGroupApp(access.application);
        setNewGroupName('');
      }}
      onCommitNewGroup={commitNewGroup}
      onCancelNewGroup={() => setNewGroupApp(null)}
      onNewGroupNameChange={setNewGroupName}
    />
  );

  return (
    <aside className="lg:col-span-4 border border-core-slate-100 rounded-lg overflow-hidden flex flex-col bg-white mb-300 lg:mb-0">
      <div className="px-200 py-150 border-b border-core-slate-100 bg-core-slate-50 flex items-center justify-between">
        <span className="text-body-xs font-bold uppercase tracking-wide text-core-slate-1100">
          {t('operatorAccessPage.display.applications')}
        </span>
      </div>
      <div className="overflow-y-auto flex-1 p-100 space-y-100 min-w-0">
        {populatedApps.map(appItem)}
        {emptyApps.length > 0 && (
          <div className="rounded-lg border border-core-slate-100">
            <div className="flex items-center gap-100 px-100 py-50 bg-core-slate-50 rounded-lg">
              <Button
                variant="neutral"
                appearance="ghost"
                fullWidth
                className="!justify-start min-w-0 flex-1 [&>span]:!justify-start [&>span]:!text-left"
                iconName={showEmptyApps ? 'chevron-down' : 'chevron-right'}
                aria-expanded={showEmptyApps}
                aria-controls={showEmptyApps ? otherAppsId : undefined}
                onClick={() => setShowEmptyApps((prev) => !prev)}
              >
                {t('operatorAccessPage.display.otherApplications')}
              </Button>
              <Tag
                variant="neutral"
                size="small"
                aria-label={t('operatorAccessPage.display.applicationCount', {
                  count: emptyApps.length,
                })}
              >
                {String(emptyApps.length)}
              </Tag>
            </div>
            {showEmptyApps && (
              <section
                id={otherAppsId}
                aria-label={t('operatorAccessPage.display.otherApplications')}
                className="p-100 space-y-100"
              >
                {emptyApps.map(appItem)}
              </section>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};
